// macadamia-app/src/macadamia-core/components/widgets/TableWidget.tsx
import React, { useState, useEffect } from 'react';
import { ITableBlock, ITableRow } from './types';
import { useDossier } from '../../dossierEditor/DossierContext';
import { v4 as uuidv4 } from 'uuid';
import Cog6Tooth from '../../icons/Cog6Tooth';
import '../styles/TableWidget.css';

interface TableWidgetProps {
    block: ITableBlock;
    onChange: (newBlock: ITableBlock) => void;
}

const TableWidget: React.FC<TableWidgetProps> = ({ block, onChange }) => {
    const { editMode: currentState } = useDossier();
    const isEditing = currentState === 'editor';
    const [showOptions, setShowOptions] = useState(false);

    // Fonction pour ajuster automatiquement la hauteur d'un textarea
    const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
    };

    // Ajuster la hauteur de tous les textarea au montage et lors des changements
    useEffect(() => {
        if (isEditing) {
            const textareas = document.querySelectorAll('.table-cell-input');
            textareas.forEach((textarea) => {
                adjustTextareaHeight(textarea as HTMLTextAreaElement);
            });
        }
    }, [block.rows, isEditing]);

    // Gérer le changement de contenu d'une cellule
    const handleCellChange = (rowIndex: number, cellIndex: number, newContent: string) => {
        const newRows = block.rows.map((row, rIdx) => {
            if (rIdx !== rowIndex) return row;
            return {
                ...row,
                cells: row.cells.map((cell, cIdx) => {
                    if (cIdx !== cellIndex) return cell;
                    return { ...cell, content: newContent };
                })
            };
        });
        onChange({ ...block, rows: newRows });
    };

    // Ajouter une ligne
    const handleAddRow = () => {
        const newRow: ITableRow = {
            id: uuidv4(),
            cells: Array.from({ length: block.columns }, () => ({
                id: uuidv4(),
                content: ''
            }))
        };
        onChange({ ...block, rows: [...block.rows, newRow] });
    };

    // Supprimer une ligne
    const handleDeleteRow = (rowIndex: number) => {
        if (block.rows.length <= 1) return; // Garder au moins une ligne
        const newRows = block.rows.filter((_, index) => index !== rowIndex);
        onChange({ ...block, rows: newRows });
    };

    // Ajouter une colonne
    const handleAddColumn = () => {
        const newRows = block.rows.map(row => ({
            ...row,
            cells: [...row.cells, { id: uuidv4(), content: '' }]
        }));
        onChange({ ...block, rows: newRows, columns: block.columns + 1 });
    };

    // Supprimer une colonne
    const handleDeleteColumn = (columnIndex: number) => {
        if (block.columns <= 1) return; // Garder au moins une colonne
        const newRows = block.rows.map(row => ({
            ...row,
            cells: row.cells.filter((_, index) => index !== columnIndex)
        }));
        onChange({ ...block, rows: newRows, columns: block.columns - 1 });
    };

    // Toggle header
    const handleToggleHeader = () => {
        onChange({ ...block, hasHeader: !block.hasHeader });
    };

    // Navigation au clavier
    const handleKeyDown = (e: React.KeyboardEvent, rowIndex: number, cellIndex: number) => {
        if (e.key === 'Tab') {
            e.preventDefault();
            const nextCellIndex = cellIndex + 1;
            const nextRowIndex = rowIndex;

            if (nextCellIndex < block.columns) {
                // Prochaine cellule dans la même ligne
                const nextCell = document.querySelector(
                    `[data-row="${nextRowIndex}"][data-cell="${nextCellIndex}"]`
                ) as HTMLTextAreaElement;
                nextCell?.focus();
            } else if (nextRowIndex + 1 < block.rows.length) {
                // Première cellule de la prochaine ligne
                const nextCell = document.querySelector(
                    `[data-row="${nextRowIndex + 1}"][data-cell="0"]`
                ) as HTMLTextAreaElement;
                nextCell?.focus();
            }
        }

        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            // Créer une nouvelle ligne si on est sur la dernière
            if (rowIndex === block.rows.length - 1) {
                handleAddRow();
                // Focus sur la première cellule de la nouvelle ligne
                setTimeout(() => {
                    const newCell = document.querySelector(
                        `[data-row="${rowIndex + 1}"][data-cell="0"]`
                    ) as HTMLTextAreaElement;
                    newCell?.focus();
                }, 0);
            }
        }
    };

    if (!isEditing) {
        // Mode lecture
        return (
            <div className="table-widget py-4">
                <table className="table-display">
                    {block.hasHeader && block.rows.length > 0 && (
                        <thead>
                            <tr>
                                {block.rows[0].cells.map((cell, cellIndex) => (
                                    <th key={cell.id} className="table-header-cell dark:text-mcdm-primary-100">
                                        {cell.content || '\u00A0'}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        {block.rows.slice(block.hasHeader ? 1 : 0).map((row, rowIndex) => (
                            <tr key={row.id}>
                                {row.cells.map((cell, cellIndex) => (
                                    <td key={cell.id} className="table-cell dark:text-mcdm-primary-100">
                                        {cell.content || '\u00A0'}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    }

    // Mode édition
    return (
        <div className="table-widget-editor relative py-4">
            {/* Options du tableau */}
            <div className="table-options-controls">
                <button
                    onClick={() => setShowOptions(!showOptions)}
                    className="table-options-button"
                    title="Options du tableau"
                >
                    <Cog6Tooth width="20px" height="20px" />
                </button>

                {showOptions && (
                    <div className="table-options-menu">
                        <button
                            onClick={handleToggleHeader}
                            className={`option-item ${block.hasHeader ? 'active' : ''}`}
                        >
                            {block.hasHeader ? '' : '○'} Ligne d'en-tête
                        </button>
                        <div className="option-divider"></div>
                        <button onClick={handleAddRow} className="option-item">
                            + Ajouter une ligne
                        </button>
                        <button onClick={handleAddColumn} className="option-item">
                            + Ajouter une colonne
                        </button>
                    </div>
                )}
            </div>

            {/* Tableau éditable */}
            <div className="table-editor-wrapper">
                <table className="table-editor">
                    {block.hasHeader && block.rows.length > 0 && (
                        <thead>
                            <tr>
                                {block.rows[0].cells.map((cell, cellIndex) => (
                                    <th key={cell.id} className="table-header-cell-editor">
                                        <textarea
                                            value={cell.content}
                                            onChange={(e) => {
                                                handleCellChange(0, cellIndex, e.target.value);
                                                adjustTextareaHeight(e.target);
                                            }}
                                            onKeyDown={(e) => handleKeyDown(e, 0, cellIndex)}
                                            placeholder="En-tête"
                                            className="table-cell-input header-input dark:text-mcdm-primary-100"
                                            data-row="0"
                                            data-cell={cellIndex}
                                            rows={1}
                                        />
                                    </th>
                                ))}
                                <th className="table-action-cell">
                                    <button
                                        onClick={() => handleDeleteColumn(block.columns - 1)}
                                        className="table-action-button delete-column"
                                        title="Supprimer la dernière colonne"
                                    >
                                        
                                    </button>
                                </th>
                            </tr>
                        </thead>
                    )}
                    <tbody>
                        {block.rows.slice(block.hasHeader ? 1 : 0).map((row, rowIndexOffset) => {
                            const rowIndex = block.hasHeader ? rowIndexOffset + 1 : rowIndexOffset;
                            return (
                                <tr key={row.id}>
                                    {row.cells.map((cell, cellIndex) => (
                                        <td key={cell.id} className="table-cell-editor">
                                            <textarea
                                                value={cell.content}
                                                onChange={(e) => {
                                                    handleCellChange(rowIndex, cellIndex, e.target.value);
                                                    adjustTextareaHeight(e.target);
                                                }}
                                                onKeyDown={(e) => handleKeyDown(e, rowIndex, cellIndex)}
                                                placeholder="Cellule"
                                                className="table-cell-input dark:text-mcdm-primary-100"
                                                data-row={rowIndex}
                                                data-cell={cellIndex}
                                                rows={1}
                                            />
                                        </td>
                                    ))}
                                    <td className="table-action-cell">
                                        <button
                                            onClick={() => handleDeleteRow(rowIndex)}
                                            className="table-action-button delete-row"
                                            title="Supprimer cette ligne"
                                        >
                                            
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TableWidget;
